import { getRouteSdkOptions } from '@repo/ssr'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
	ArrowRight,
	BookOpen,
	Boxes,
	CircleCheck,
	Gauge,
	ShieldCheck,
	Webhook
} from 'lucide-react'
import type { ReactNode } from 'react'
import { z } from 'zod'
import { MarketingHeader, PortalFooter } from '@/components/portal-shell'
import { type TKey, translateApp } from '@/lib/i18n'
import { fetchLandingMetrics } from '@/lib/landing-metrics'
import { fetchPortalLandingSessionState } from '@/lib/portal-session'

const landingSearchSchema = z.object({
	accountDeleted: z
		.union([z.literal(true), z.literal('true')])
		.optional()
		.catch(undefined)
})

export const Route = createFileRoute('/')({
	ssr: true,
	validateSearch: landingSearchSchema,
	headers: () => ({
		'Cache-Control': 'private, no-store',
		Vary: 'Cookie, Authorization, X-Session-Token'
	}),
	loader: async (routeContext) => {
		const sdkOptions = getRouteSdkOptions(routeContext, { auth: true })
		const [sessionState, landingMetrics] = await Promise.all([
			fetchPortalLandingSessionState(sdkOptions),
			fetchLandingMetrics(sdkOptions)
		])
		return { sessionState, landingMetrics }
	},
	component: LandingPage
})

function LandingPage() {
	const { appLanguage } = Route.useRouteContext()
	const { sessionState, landingMetrics } = Route.useLoaderData()
	const search = Route.useSearch()
	const t = (key: TKey, options?: Record<string, string>) =>
		translateApp(appLanguage, key, options)
	const acquisitionTarget =
		sessionState.status === 'guest' ? '/register' : '/dashboard'

	return (
		<div className='portal-page min-h-svh'>
			<MarketingHeader
				appLanguage={appLanguage}
				sessionState={sessionState}
			/>
			<main>
				{search.accountDeleted ? (
					<output
						aria-live='polite'
						className='portal-container landing-account-deleted mt-5 flex items-center gap-2.5'
					>
						<CircleCheck aria-hidden='true' />
						<p>{t('common:landing.account_deleted')}</p>
					</output>
				) : null}
				<section className='landing-hero relative overflow-hidden py-[clamp(3.875rem,12vw,8.75rem)] pb-[3.125rem] lg:min-h-[calc(100svh-4.125rem)] lg:pb-[3.625rem]'>
					<div className='portal-container landing-hero__grid grid items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(26.25rem,28.5rem)] lg:gap-[clamp(4rem,8.3vw,5.9375rem)]'>
						<div className='landing-hero__content'>
							<p className='portal-eyebrow'>
								<span aria-hidden='true'>★</span>{' '}
								{t('common:landing.hero_eyebrow')}
							</p>
							<h1>
								{t('common:landing.hero_title_main')}{' '}
								<em>{t('common:landing.hero_title_accent')}</em>
							</h1>
							<p className='portal-lead'>
								{t('common:landing.hero_description')}
							</p>
							<div className='landing-hero__actions mt-[2.125rem] grid gap-3 sm:mt-[3.75rem] sm:flex sm:flex-wrap'>
								<Link
									className='portal-button portal-button--primary'
									to={acquisitionTarget}
								>
									{t('common:landing.hero_try_api')}
								</Link>
								<Link
									className='hero-dashboard-link inline-flex min-h-11 items-center justify-center gap-2 px-2 font-bold text-sm'
									to='/dashboard'
								>
									{t('common:landing.hero_dashboard')}{' '}
									<ArrowRight aria-hidden='true' />
								</Link>
							</div>
							<LandingMetrics
								appLanguage={appLanguage}
								metrics={landingMetrics}
								t={t}
							/>
						</div>
						<ApiTerminal label={t('common:landing.api_example')} />
					</div>
				</section>

				<section className='portal-section border-(--portal-border) border-t py-[clamp(3.625rem,9vw,6.5rem)]'>
					<div className='portal-container'>
						<div className='portal-section__heading mb-12 max-w-[38.125rem]'>
							<p className='portal-eyebrow'>
								{t('common:landing.features_eyebrow')}
							</p>
							<h2 className='portal-heading'>
								{t('common:landing.features_title')}
							</h2>
							<p className='portal-lead'>
								{t('common:landing.features_description')}
							</p>
						</div>
						<div className='portal-grid grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
							<Feature
								icon={<Boxes />}
								title={t(
									'common:landing.feature_catalog_title'
								)}
							>
								{t(
									'common:landing.feature_catalog_description'
								)}
							</Feature>
							<Feature
								icon={<Gauge />}
								title={t('common:landing.feature_rate_title')}
							>
								{t('common:landing.feature_rate_description')}
							</Feature>
							<Feature
								icon={<Webhook />}
								title={t(
									'common:landing.feature_webhook_title'
								)}
							>
								{t(
									'common:landing.feature_webhook_description'
								)}
							</Feature>
						</div>
					</div>
				</section>

				<section
					className='portal-section border-(--portal-border) border-t py-[clamp(3.625rem,9vw,6.5rem)]'
					id='docs'
				>
					<div className='portal-container'>
						<div className='portal-section__heading mb-12 max-w-[38.125rem]'>
							<p className='portal-eyebrow'>
								<BookOpen aria-hidden='true' />{' '}
								{t('common:landing.docs_eyebrow')}
							</p>
							<h2 className='portal-heading'>
								{t('common:landing.docs_title')}
							</h2>
						</div>
						<div className='portal-grid grid gap-5 md:grid-cols-2'>
							<pre className='code-surface'>
								<code>
									{t('common:landing.docs_code_comment')}
									{'\n'}
									<span className='token-key'>GET</span>
									{
										' /v1/cards?set=OP06&page=1&perPage=20\n\n'
									}
									<span className='token-key'>HEADERS</span>
									{'\nX-API-Key: slk_••••••••\n\n'}
									<span className='token-key'>RESPONSE</span>
									{' 200\n{\n  '}
									<span className='token-string'>
										&quot;data&quot;
									</span>
									{': [...],\n  '}
									<span className='token-string'>
										&quot;meta&quot;
									</span>
									{': { '}
									<span className='token-string'>
										&quot;page&quot;
									</span>
									{': '}
									<span className='token-number'>1</span>
									{' }\n}'}
								</code>
							</pre>
							<div className='endpoints'>
								<Endpoint
									method='GET'
									path='/v1/cards'
									text={t('common:landing.endpoint_cards')}
								/>
								<Endpoint
									method='GET'
									path='/v1/cards/:id'
									text={t('common:landing.endpoint_card')}
								/>
								<Endpoint
									method='GET'
									path='/v1/sets'
									text={t('common:landing.endpoint_sets')}
								/>
								<Endpoint
									method='POST'
									path='/v1/decks/validate'
									text={t('common:landing.endpoint_validate')}
								/>
							</div>
						</div>
					</div>
				</section>

				<section
					className='portal-section border-(--portal-border) border-t py-[clamp(3.625rem,9vw,6.5rem)]'
					id='prezzi'
				>
					<div className='portal-container'>
						<div className='portal-section__heading mb-12 max-w-[38.125rem]'>
							<p className='portal-eyebrow'>
								{t('common:landing.pricing_eyebrow')}
							</p>
							<h2 className='portal-heading'>
								{t('common:landing.pricing_title')}
							</h2>
						</div>
						<div className='portal-grid grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
							<PriceCard
								description={t(
									'common:landing.pricing_description_starter'
								)}
								items={[
									t(
										'common:landing.pricing_item_daily_requests'
									),
									t('common:landing.pricing_item_read'),
									t('common:landing.pricing_item_images_low')
								]}
								name='Starter'
								price='€0'
								t={t}
							/>
							<PriceCard
								description={t(
									'common:landing.pricing_description_developer'
								)}
								items={[
									t(
										'common:landing.pricing_item_monthly_requests'
									),
									t(
										'common:landing.pricing_item_read_validate'
									),
									t(
										'common:landing.pricing_item_images_full'
									),
									t('common:landing.pricing_item_webhooks')
								]}
								name='Developer'
								popular
								price='€9'
								t={t}
							/>
							<PriceCard
								description={t(
									'common:landing.pricing_description_enterprise'
								)}
								items={[
									t('common:landing.pricing_item_unlimited'),
									t('common:landing.pricing_item_sla'),
									t('common:landing.pricing_item_support')
								]}
								name='Enterprise'
								price={t(
									'common:landing.pricing_enterprise_price'
								)}
								t={t}
							/>
						</div>
					</div>
				</section>

				<section className='portal-section border-(--portal-border) border-t py-[clamp(3.625rem,9vw,6.5rem)]'>
					<div className='portal-container'>
						<div className='portal-section__heading mb-12 max-w-[38.125rem]'>
							<p className='portal-eyebrow'>
								<ShieldCheck aria-hidden='true' />{' '}
								{t('common:landing.faq_eyebrow')}
							</p>
							<h2 className='portal-heading'>
								{t('common:landing.faq_title')}
							</h2>
						</div>
						<div className='faq-list'>
							<Faq question={t('common:landing.faq_1_question')}>
								{t('common:landing.faq_1_answer')}
							</Faq>
							<Faq question={t('common:landing.faq_2_question')}>
								{t('common:landing.faq_2_answer')}
							</Faq>
							<Faq question={t('common:landing.faq_3_question')}>
								{t('common:landing.faq_3_answer')}
							</Faq>
						</div>
					</div>
				</section>

				<section className='portal-section border-(--portal-border) border-t py-[clamp(3.625rem,9vw,6.5rem)]'>
					<div className='portal-container'>
						<div className='cta-band px-6 py-[3.75rem] text-center'>
							<h2>{t('common:landing.cta_title')}</h2>
							<p>{t('common:landing.cta_description')}</p>
							<Link
								className='portal-button portal-button--primary'
								to='/dashboard'
							>
								{t('common:header.dashboard')}{' '}
								<ArrowRight aria-hidden='true' />
							</Link>
						</div>
					</div>
				</section>
			</main>
			<PortalFooter appLanguage={appLanguage} />
		</div>
	)
}

function ApiTerminal(props: { label: string }) {
	return (
		<section aria-label={props.label} className='terminal overflow-hidden'>
			<div className='terminal__bar flex items-center gap-1.5 px-3.5 py-3'>
				<div
					aria-hidden='true'
					className='terminal__lights flex gap-1.5'
				>
					<span />
					<span />
					<span />
				</div>
				<span className='terminal__title'>bash</span>
			</div>
			<pre>
				<code>
					<span className='token-key'>$</span>
					{' curl -H '}
					<span className='token-string'>
						&quot;X-API-Key: sl_live_*****&quot;
					</span>
					{
						' \\\n  https://api.sealedger.cards/v1/cards?name=Luffy\n\n'
					}
					<span className='token-success'>200 OK</span>
					{'\n{\n  '}
					<span className='token-key'>&quot;cards&quot;</span>
					{': [\n    {\n      '}
					<span className='token-key'>&quot;id&quot;</span>
					{': '}
					<span className='token-string'>&quot;OP01-001&quot;</span>,
					{'\n      '}
					<span className='token-key'>&quot;name&quot;</span>
					{': '}
					<span className='token-string'>
						&quot;Monkey D. Luffy&quot;
					</span>
					{',\n      '}
					<span className='token-key'>&quot;type&quot;</span>
					{': '}
					<span className='token-string'>&quot;Leader&quot;</span>,
					{'\n      '}
					<span className='token-key'>&quot;cost&quot;</span>
					{': '}
					<span className='token-number'>4</span>
					{'\n    }\n  ]\n}'}
				</code>
			</pre>
		</section>
	)
}

function LandingMetrics(props: {
	appLanguage: 'en' | 'it'
	metrics: Awaited<ReturnType<typeof fetchLandingMetrics>>
	t: (key: TKey, options?: Record<string, string>) => string
}) {
	if (!props.metrics) {
		return null
	}

	const numberFormatter = new Intl.NumberFormat(props.appLanguage)
	const stats = [
		{
			label: props.t('common:landing.stat_catalog_printings'),
			value: numberFormatter.format(props.metrics.catalogPrintings)
		},
		...(props.metrics.requestsLast30Days === null
			? []
			: [
					{
						label: props.t(
							'common:landing.stat_requests_last_30_days'
						),
						value: numberFormatter.format(
							props.metrics.requestsLast30Days
						)
					}
				])
	]

	if (props.metrics.averageLatencyMsLast30Days !== null) {
		const roundedLatency = Math.round(
			props.metrics.averageLatencyMsLast30Days
		)
		stats.push({
			label: props.t('common:landing.stat_average_latency'),
			value: props.t('common:dashboard.latency_value', {
				value: String(roundedLatency)
			})
		})
	}

	const dataThrough = props.metrics.dataThrough
		? new Intl.DateTimeFormat(props.appLanguage, {
				dateStyle: 'medium',
				timeStyle: 'short'
			}).format(new Date(props.metrics.dataThrough))
		: null

	return (
		<div className='mt-7'>
			<div className='landing-hero__stats flex flex-wrap gap-y-2.5 text-xs'>
				{stats.map((stat) => (
					<span key={stat.label}>
						<strong>{stat.value}</strong> {stat.label}
					</span>
				))}
			</div>
			{dataThrough ? (
				<p className='mt-2 text-xs'>
					{props.t('common:landing.metrics_updated', {
						date: dataThrough
					})}
				</p>
			) : null}
		</div>
	)
}

function Feature(props: { icon: ReactNode; title: string; children: string }) {
	return (
		<article className='feature-card'>
			<div className='feature-icon'>{props.icon}</div>
			<h2>{props.title}</h2>
			<p>{props.children}</p>
		</article>
	)
}

function Endpoint(props: { method: string; path: string; text: string }) {
	return (
		<article className='endpoint'>
			<div>
				<span className='method-pill'>{props.method}</span>
				<p>{props.text}</p>
			</div>
			<code>{props.path}</code>
		</article>
	)
}

function PriceCard(props: {
	name: string
	price: string
	description: string
	items: string[]
	popular?: boolean
	t: (key: TKey, options?: Record<string, string>) => string
}) {
	return (
		<article
			className={
				props.popular
					? 'portal-card pricing-card pricing-card--popular'
					: 'portal-card pricing-card'
			}
		>
			{props.popular ? (
				<span className='pricing-badge'>
					{props.t('common:landing.pricing_popular')}
				</span>
			) : null}
			<h2 className='portal-card__title'>{props.name}</h2>
			<p className='stat-label'>{props.description}</p>
			<div className='price'>
				{props.price}
				<span>
					{props.price.startsWith('€')
						? props.t('common:landing.pricing_month')
						: ''}
				</span>
			</div>
			<Link
				className={
					props.popular
						? 'portal-button portal-button--primary'
						: 'portal-button portal-button--secondary'
				}
				to='/login'
			>
				{props.t('common:landing.pricing_select', { name: props.name })}
			</Link>
			<ul>
				{props.items.map((item) => (
					<li key={item}>{item}</li>
				))}
			</ul>
		</article>
	)
}

function Faq(props: { question: string; children: string }) {
	return (
		<details>
			<summary>{props.question}</summary>
			<p>{props.children}</p>
		</details>
	)
}
